import React, { useLayoutEffect } from 'react'
import { Box, ButtonBase } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { __t } from '../../../../i18n/t'
import { C, focusRing } from '../ui/tokens'
import { useElementWidth } from './useElementWidth'

export interface StepperItem { key: string; label: string }

const MIN_PER_STEP = 150

const DotLabel: React.FC<{ text: string; cur: boolean; reach: boolean }> = ({ text, cur, reach }) => (
  <Box aria-hidden component="span" sx={{ fontSize: 14, fontWeight: cur ? 700 : 500, color: reach ? C.ink : C.muted, whiteSpace: 'nowrap' }}>{text}</Box>
)

/**
 * Bolinha numerada da etapa; a visitada vira botão (alvo de toque de 40 px mesmo com a bolinha de 26).
 * O nome acessível vem de um `aria-label` só ("2. Postos e valores"), sem repetir o número que a bolinha já mostra.
 */
const Dot: React.FC<{ index: number; item: StepperItem; current: number; maxStep: number; busy: boolean; onGo: (i: number) => void; showLabel: boolean }> = ({ index, item, current, maxStep, busy, onGo, showLabel }) => {
  const cur = index === current
  const reach = index <= maxStep
  const done = !cur && reach && (index < current || index < maxStep)
  const name = done ? __t('%{n}. %{etapa}, concluída', { n: index + 1, etapa: item.label }) : __t('%{n}. %{etapa}', { n: index + 1, etapa: item.label })
  return (
    <ButtonBase
      onClick={() => onGo(index)}
      disabled={!reach || busy}
      aria-current={cur ? 'step' : undefined}
      aria-label={name}
      focusRipple={false}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: showLabel ? 0.5 : 0.75, py: 0.75, minHeight: 40, minWidth: 40, justifyContent: 'center', borderRadius: '8px',
        cursor: reach && !cur && !busy ? 'pointer' : 'default', '&.Mui-focusVisible': focusRing, '&.Mui-disabled': { opacity: 1 },
      }}
    >
      <Box aria-hidden sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: cur || done ? C.green : '#e2e8f0', color: C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 700, flex: 'none' }}>
        {done ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
      </Box>
      {showLabel ? <DotLabel text={item.label} cur={cur} reach={reach} /> : null}
    </ButtonBase>
  )
}

interface ListProps { steps: StepperItem[]; current: number; maxStep: number; busy: boolean; onGo: (i: number) => void }

/** Celular: "Etapa 2 de 5 · Postos e valores" e só as bolinhas. */
const CompactSteps: React.FC<ListProps> = ({ steps, current, maxStep, busy, onGo }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, fontSize: 14 }}>
      <Box component="span" sx={{ color: C.quiet }}>{__t('Etapa %{n} de %{total}', { n: current + 1, total: steps.length })}</Box>
      <Box component="span" sx={{ fontWeight: 700, color: C.ink }}>{steps[current]?.label}</Box>
    </Box>
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', alignItems: 'center', ml: -0.75 }}>
      {steps.map((s, i) => (
        <Box component="li" key={s.key} sx={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? '1 1 auto' : '0 0 auto', minWidth: 0 }}>
          <Dot index={i} item={s} current={current} maxStep={maxStep} busy={busy} onGo={onGo} showLabel={false} />
          {i < steps.length - 1 ? <Box aria-hidden sx={{ flex: '1 1 8px', minWidth: 8, height: '1px', bgcolor: '#bdbdbd' }} /> : null}
        </Box>
      ))}
    </Box>
  </Box>
)

/** Tela larga: bolinha e nome de cada etapa na mesma linha. */
const FullSteps: React.FC<ListProps> = ({ steps, current, maxStep, busy, onGo }) => (
  <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
    {steps.map((s, i) => (
      <Box component="li" key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: i < steps.length - 1 ? '1 1 auto' : '0 0 auto', minWidth: 0 }}>
        <Dot index={i} item={s} current={current} maxStep={maxStep} busy={busy} onGo={onGo} showLabel />
        {i < steps.length - 1 ? <Box aria-hidden sx={{ flex: '1 1 16px', minWidth: 16, height: '1px', bgcolor: '#bdbdbd' }} /> : null}
      </Box>
    ))}
  </Box>
)

/**
 * Etapas do cadastro. A etapa visitada é clicável; a que ainda não foi alcançada fica travada.
 * Quando os rótulos não cabem na linha (celular), mostra "Etapa 2 de 5 · Postos e valores" e só as bolinhas.
 */
export const Stepper: React.FC<{
  steps: StepperItem[]
  current: number
  maxStep: number
  busy: boolean
  onGo: (i: number) => void
  onCompactChange?: (compact: boolean) => void
}> = ({ steps, current, maxStep, busy, onGo, onCompactChange }) => {
  const [ref, width] = useElementWidth<HTMLDivElement>()
  const compact = width > 0 && width < steps.length * MIN_PER_STEP
  // o cartão esconde a linha "Etapa N de M" quando esta barra já a mostra
  useLayoutEffect(() => { onCompactChange?.(compact) }, [compact, onCompactChange])
  const List = compact ? CompactSteps : FullSteps

  return (
    <Box ref={ref} component="nav" aria-label={__t('Etapas do cadastro')} sx={{ minWidth: 0 }}>
      <List steps={steps} current={current} maxStep={maxStep} busy={busy} onGo={onGo} />
    </Box>
  )
}
